import { signRequest } from "@shared/crypto/signRequest";
import { fromBase64, toBase64 } from "@shared/crypto/useCrypto";
import { apiUrl } from "@shared/lib/apiUrl";

import type { FileAttachment } from "shared";

// Чанковый E2E-транспорт файлов. Файл режется на plaintext-чанки по CHUNK байт,
// каждый шифруется отдельным AES-GCM (IV = baseNonce(8) || uint32BE(index)),
// поэтому память ограничена размером чанка (а не файла), можно показывать прогресс
// и — в будущем — расшифровывать по диапазонам (стриминг видео через SW).
// Заливка — последовательными запросами по чанку (первый POST создаёт файл,
// остальные PATCH дописывают): стриминговое тело fetch требует HTTP/2, а чанковые
// запросы работают везде (dev-прокси, nginx, Tauri).
// Универсально: принимает File или Blob → подходит и файлам, и аудио/видео-
// сообщениям (те просто передадут записанный Blob).

const CHUNK = 4 * 1024 * 1024; // 4 МиБ plaintext на чанк
const TAG = 16; // GCM-тег добавляется к каждому чанку

type ProgressCb = (fraction: number) => void;

// IV чанка: 8 байт baseNonce + 4 байта big-endian счётчика (уникален в пределах
// файла; ключ у каждого файла свой, так что переиспользования IV нет).
function chunkIv(
    baseNonce: Uint8Array,
    index: number
): Uint8Array<ArrayBuffer> {
    const iv = new Uint8Array(12);
    iv.set(baseNonce, 0);
    new DataView(iv.buffer).setUint32(8, index, false);
    return iv;
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array<ArrayBuffer> {
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
}

// Отправка одного чанка через XHR: в отличие от fetch, XHR даёт байтовый
// upload-прогресс (важно для мелких файлов, где чанк один и fetch показал бы
// только 0% → 100%).
function xhrSend(
    method: string,
    url: string,
    headers: Record<string, string>,
    body: ArrayBuffer,
    onBytes?: (loaded: number) => void,
    signal?: AbortSignal
): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url);
        for (const [k, v] of Object.entries(headers)) {
            xhr.setRequestHeader(k, v);
        }
        xhr.upload.onprogress = (e) => {
            onBytes?.(e.loaded);
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.responseText);
            } else {
                reject(
                    new Error(
                        `Upload failed: HTTP ${xhr.status} ${xhr.responseText.slice(0, 120)}`
                    )
                );
            }
        };
        // status 0 — запрос не дошёл: сеть, CSP или срезанный CORS-preflight.
        xhr.onerror = () =>
            reject(new Error("Upload failed: network/CORS/CSP (status 0)"));
        xhr.onabort = () =>
            reject(new DOMException("Upload aborted", "AbortError"));
        signal?.addEventListener("abort", () => xhr.abort(), { once: true });
        xhr.send(body);
    });
}

export function useChunkedTransfer() {
    // Заливка: шифруем и отправляем чанки последовательными запросами (первый
    // POST создаёт файл и возвращает fileId, следующие PATCH дописывают).
    // Прогресс — байтовый (XHR upload). Возвращает готовый FileAttachment.
    async function upload(
        blob: Blob,
        chatId: string,
        meta: { name: string; mimeType: string },
        onProgress?: ProgressCb,
        signal?: AbortSignal
    ): Promise<FileAttachment> {
        const key = await crypto.subtle.generateKey(
            { name: "AES-GCM", length: 256 },
            true,
            ["encrypt", "decrypt"]
        );
        const keyRaw = await crypto.subtle.exportKey("raw", key);
        const baseNonce = crypto.getRandomValues(new Uint8Array(8));
        const size = blob.size;
        const chunkCount = Math.max(1, Math.ceil(size / CHUNK));

        // В attachment.url хранится относительный путь (переносим между
        // окружениями); абсолютный адрес собирается в момент запроса.
        const basePath = `/api/files/${chatId}`;
        let fileUrl = "";
        let fileId = "";

        try {
            for (let index = 0; index < chunkCount; index++) {
                if (signal?.aborted) {
                    throw new DOMException("Upload aborted", "AbortError");
                }
                const start = index * CHUNK;
                const end = Math.min(size, start + CHUNK);
                const plain = await blob.slice(start, end).arrayBuffer();
                const ct = await crypto.subtle.encrypt(
                    { name: "AES-GCM", iv: chunkIv(baseNonce, index) },
                    key,
                    plain
                );

                const method = index === 0 ? "POST" : "PATCH";
                const url = apiUrl(
                    index === 0 ? basePath : `${basePath}/${fileId}`
                );
                const text = await xhrSend(
                    method,
                    url,
                    {
                        "Content-Type": "application/octet-stream",
                        ...(await signRequest(method, url)),
                    },
                    ct,
                    // Байтовый прогресс внутри чанка: отправлено ранее + в полёте
                    // (loaded считается по шифротексту — погрешность в размер тега).
                    (loaded) => {
                        if (size) {
                            onProgress?.(Math.min(1, (start + loaded) / size));
                        }
                    },
                    signal
                );
                if (index === 0) {
                    ({ fileUrl, fileId } = JSON.parse(text) as {
                        fileUrl: string;
                        fileId: string;
                    });
                }
                onProgress?.(size ? end / size : 1);
            }
        } catch (e) {
            // Отмена/обрыв на середине: подчищаем недокачанный файл на сервере.
            if (fileUrl) {
                void deleteFile(fileUrl);
            }
            throw e;
        }

        return {
            url: fileUrl,
            key: toBase64(keyRaw),
            name: meta.name,
            size,
            mimeType: meta.mimeType,
            baseNonce: toBase64(baseNonce),
            chunkSize: CHUNK,
        };
    }

    // Скачивание: тянем шифротекст потоком и расшифровываем чанк-за-чанком
    // (память ограничена ~1 чанком; собранный blob — целиком в памяти один раз).
    // Легаси-файлы (одним блобом, поле iv) — старым путём.
    async function download(
        att: FileAttachment,
        onProgress?: ProgressCb
    ): Promise<Blob> {
        const res = await fetch(apiUrl(att.url), {
            headers: await signRequest("GET", att.url),
        });
        if (!res.ok) {
            throw new Error(`Failed to download file: HTTP ${res.status}`);
        }
        const key = await crypto.subtle.importKey(
            "raw",
            fromBase64(att.key),
            { name: "AES-GCM" },
            false,
            ["decrypt"]
        );

        // Легаси: единый IV на весь файл.
        if (!att.baseNonce || !att.chunkSize) {
            const data = await res.arrayBuffer();
            const plain = await crypto.subtle.decrypt(
                {
                    name: "AES-GCM",
                    iv: new Uint8Array(fromBase64(att.iv ?? "")),
                },
                key,
                data
            );
            return new Blob([plain], { type: att.mimeType });
        }

        const baseNonce = new Uint8Array(fromBase64(att.baseNonce));
        const chunkSize = att.chunkSize;
        const chunkCount = Math.max(1, Math.ceil(att.size / chunkSize));
        // Длина ШИФРОтекста чанка i = plaintext-длина + тег.
        const ctLen = (i: number) =>
            Math.min(chunkSize, att.size - i * chunkSize) + TAG;

        const reader = res.body?.getReader();
        if (!reader) {
            throw new Error("No response body");
        }
        const parts: BlobPart[] = [];
        let buf = new Uint8Array(0);
        let i = 0;
        let done = false;
        while (!done) {
            const r = await reader.read();
            done = r.done;
            if (r.value) {
                buf = concat(buf, r.value);
            }
            while (i < chunkCount && buf.length >= ctLen(i)) {
                const len = ctLen(i);
                const ct = buf.slice(0, len);
                buf = buf.slice(len);
                const plain = await crypto.subtle.decrypt(
                    { name: "AES-GCM", iv: chunkIv(baseNonce, i) },
                    key,
                    ct
                );
                parts.push(plain);
                i++;
                onProgress?.(i / chunkCount);
            }
        }
        return new Blob(parts, { type: att.mimeType });
    }

    // Удаление файла на сервере (отмена загрузки, удаление вложения до отправки,
    // в будущем — «удалить у всех»). Идемпотентно.
    async function deleteFile(fileUrl: string): Promise<void> {
        await fetch(apiUrl(fileUrl), {
            method: "DELETE",
            headers: await signRequest("DELETE", fileUrl),
        });
    }

    return { upload, download, deleteFile };
}
